import { useState, useCallback } from 'react'
import {
  Stack,
  IColumn,
  IconButton,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  TextField,
  Toggle,
  Dropdown,
  IDropdownOption,
  Dialog,
  DialogType,
  DialogFooter,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi, useMutation } from '../hooks/useApi'
import { useRoles } from '../hooks/useRoles'
import { DataTable } from '../components/DataTable'
import type { BusinessUnit, BusinessUnitFormData } from '../types'

const emptyFormData: BusinessUnitFormData = {
  name: '',
  description: '',
  code: '',
  parentBusinessUnitId: undefined,
  isActive: true,
}

export function BusinessUnits() {
  const { isAdmin } = useRoles()
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [editingUnit, setEditingUnit] = useState<BusinessUnit | null>(null)
  const [formData, setFormData] = useState<BusinessUnitFormData>(emptyFormData)
  const [deleteDialogUnit, setDeleteDialogUnit] = useState<BusinessUnit | null>(null)

  const fetchBusinessUnits = useCallback(
    () => apiService.getBusinessUnits({ includeInactive: true }),
    []
  )

  const {
    data: businessUnits,
    loading,
    error,
    refetch,
  } = useApi<BusinessUnit[]>(fetchBusinessUnits, [])

  const createMutation = useMutation((data: BusinessUnitFormData) =>
    apiService.createBusinessUnit(data)
  )
  const updateMutation = useMutation(({ id, data }: { id: number; data: BusinessUnitFormData }) =>
    apiService.updateBusinessUnit(id, data)
  )
  const deleteMutation = useMutation((id: number) => apiService.deleteBusinessUnit(id))

  const parentOptions: IDropdownOption[] = [
    { key: '', text: 'None (Top Level)' },
    ...(businessUnits
      ?.filter(bu => bu.businessUnitId !== editingUnit?.businessUnitId)
      .map(bu => ({ key: bu.businessUnitId, text: bu.name })) || []),
  ]

  const columns: IColumn[] = [
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 200,
      onRender: (item: BusinessUnit) => (
        <Stack title={`${item.name}${item.parentBusinessUnitName ? `\nParent: ${item.parentBusinessUnitName}` : ''}`}>
          <span style={{ fontWeight: 500 }}>{item.name}</span>
          {item.parentBusinessUnitName && (
            <span style={{ fontSize: 12, color: '#605e5c' }}>
              Parent: {item.parentBusinessUnitName}
            </span>
          )}
        </Stack>
      ),
    },
    {
      key: 'description',
      name: 'Description',
      fieldName: 'description',
      minWidth: 250,
      onRender: (item: BusinessUnit) => {
        const value = item.description || '-'
        return <span title={value}>{value}</span>
      },
    },
    {
      key: 'code',
      name: 'Code',
      fieldName: 'code',
      minWidth: 120,
      onRender: (item: BusinessUnit) => {
        const value = item.code || '-'
        return <span title={value}>{value}</span>
      },
    },
    {
      key: 'applications',
      name: 'Applications',
      minWidth: 100,
      onRender: (item: BusinessUnit) => item.applicationCount || 0,
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 90,
      onRender: (item: BusinessUnit) => (
        <span
          title={item.isActive ? 'Active' : 'Inactive'}
          style={{
            padding: '4px 8px',
            borderRadius: 12,
            fontSize: 12,
            backgroundColor: item.isActive ? '#dff6dd' : '#f3f2f1',
            color: item.isActive ? '#107c10' : '#605e5c',
          }}
        >
          {item.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      name: '',
      minWidth: 80,
      maxWidth: 80,
      isResizable: false,
      onRender: (item: BusinessUnit) =>
        isAdmin ? (
          <Stack horizontal tokens={{ childrenGap: 4 }}>
            <IconButton
              iconProps={{ iconName: 'Edit' }}
              title="Edit"
              onClick={(e) => {
                e.stopPropagation()
                openEditPanel(item)
              }}
            />
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Delete"
              onClick={(e) => {
                e.stopPropagation()
                setDeleteDialogUnit(item)
              }}
            />
          </Stack>
        ) : null,
    },
  ]

  const openEditPanel = (unit?: BusinessUnit) => {
    if (unit) {
      setEditingUnit(unit)
      setFormData({
        name: unit.name,
        description: unit.description || '',
        code: unit.code || '',
        parentBusinessUnitId: unit.parentBusinessUnitId,
        isActive: unit.isActive,
      })
    } else {
      setEditingUnit(null)
      setFormData(emptyFormData)
    }
    setIsEditPanelOpen(true)
  }

  const handleSave = async () => {
    if (editingUnit) {
      const result = await updateMutation.mutate({ id: editingUnit.businessUnitId, data: formData })
      if (result) {
        setIsEditPanelOpen(false)
        refetch()
      }
    } else {
      const result = await createMutation.mutate(formData)
      if (result) {
        setIsEditPanelOpen(false)
        refetch()
      }
    }
  }

  const handleDelete = async () => {
    if (deleteDialogUnit) {
      await deleteMutation.mutate(deleteDialogUnit.businessUnitId)
      setDeleteDialogUnit(null)
      refetch()
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <div>
            <h1 className="page-title">Business Units</h1>
            <p className="page-description">
              Organize applications by business unit or department
            </p>
          </div>
          {isAdmin && (
            <PrimaryButton
              iconProps={{ iconName: 'Add' }}
              text="Add Business Unit"
              onClick={() => openEditPanel()}
            />
          )}
        </Stack>
      </div>

      {/* Results */}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          styles={{ root: { marginBottom: 16 } }}
        >
          {error}
        </MessageBar>
      )}

      <div className="card">
        <DataTable
          items={businessUnits || []}
          columns={columns}
          loading={loading}
          emptyMessage="No business units found"
        />
      </div>

      {/* Edit/Create Panel */}
      <Panel
        isOpen={isEditPanelOpen}
        onDismiss={() => setIsEditPanelOpen(false)}
        headerText={editingUnit ? 'Edit Business Unit' : 'Add Business Unit'}
        type={PanelType.medium}
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text="Save"
              onClick={handleSave}
              disabled={createMutation.loading || updateMutation.loading}
            />
            <DefaultButton text="Cancel" onClick={() => setIsEditPanelOpen(false)} />
          </Stack>
        )}
        isFooterAtBottom
      >
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 20 } }}>
          {(createMutation.error || updateMutation.error) && (
            <MessageBar messageBarType={MessageBarType.error}>
              {createMutation.error || updateMutation.error}
            </MessageBar>
          )}
          <TextField
            label="Name"
            value={formData.name}
            onChange={(_, val) => setFormData({ ...formData, name: val || '' })}
            required
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(_, val) => setFormData({ ...formData, description: val })}
            multiline
            rows={3}
          />
          <TextField
            label="Code"
            value={formData.code}
            onChange={(_, val) => setFormData({ ...formData, code: val })}
          />
          <Dropdown
            label="Parent Business Unit"
            options={parentOptions}
            selectedKey={formData.parentBusinessUnitId || ''}
            onChange={(_, opt) =>
              setFormData({
                ...formData,
                parentBusinessUnitId: opt?.key ? Number(opt.key) : undefined,
              })
            }
          />
          <Toggle
            label="Active"
            checked={formData.isActive}
            onChange={(_, checked) => setFormData({ ...formData, isActive: !!checked })}
            onText="Active"
            offText="Inactive"
          />
        </Stack>
      </Panel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!deleteDialogUnit}
        onDismiss={() => setDeleteDialogUnit(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete Business Unit',
          subText: `Are you sure you want to delete "${deleteDialogUnit?.name}"? Applications in this unit will become unassigned.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text="Delete"
            onClick={handleDelete}
            styles={{ root: { backgroundColor: '#a4262c', borderColor: '#a4262c' } }}
          />
          <DefaultButton text="Cancel" onClick={() => setDeleteDialogUnit(null)} />
        </DialogFooter>
      </Dialog>
    </div>
  )
}
